import { Link, useParams } from "react-router-dom";
import useSWR from "swr";

import { ArtRow, GuideCard } from "../components/KbCards";
import { Icon } from "../components/Icon";
import { kbCategoryMeta } from "../config/kbCategories";
import { api, type KbPublicPage } from "../services/api";

/** A single category's listing: guided walkthroughs spotlight + article list. */
export function KbCategory() {
  const { category = "" } = useParams<{ category: string }>();
  const meta = kbCategoryMeta(category);

  const { data } = useSWR<KbPublicPage>(
    `/api/kb?category=${encodeURIComponent(category)}`,
    () => api.kb.list({ category, page_size: 100 }),
  );

  const items = data?.items ?? [];
  const guides = items.filter((a) => a.article_type === "guide");
  const articles = items.filter((a) => a.article_type !== "guide");

  return (
    <div className="kb-page">
      <div className="crumb">
        <Link className="back" to="/kb/categories">
          <Icon name="arrowLeft" size={15} />
          All categories
        </Link>
      </div>
      <div className="kb-cathead">
        <span className={`kb-cathead-ico tint-${meta.tint}`}>
          <Icon name={meta.icon} size={26} />
        </span>
        <div>
          <h1 className="page-title">{category}</h1>
          {meta.blurb && (
            <p className="page-subtitle" style={{ marginTop: 4 }}>
              {meta.blurb}
            </p>
          )}
        </div>
      </div>

      {guides.length > 0 && (
        <section className="kb-section" style={{ marginTop: 28 }}>
          <div className="kb-section-head">
            <div className="kb-section-title">
              Guided walkthroughs<span className="sub">{guides.length}</span>
            </div>
          </div>
          <div className="kb-guides">
            {guides.map((g) => (
              <GuideCard key={g.id} article={g} />
            ))}
          </div>
        </section>
      )}

      <section className="kb-section">
        <div className="kb-section-head">
          <div className="kb-section-title">
            {guides.length ? "More articles" : "Articles"}
            <span className="sub">{articles.length}</span>
          </div>
        </div>
        {articles.length > 0 ? (
          <div className="kb-art-list">
            {articles.map((a) => (
              <ArtRow key={a.id} article={a} />
            ))}
          </div>
        ) : (
          <div style={{ padding: "20px 4px", color: "var(--iq-text-muted)", fontSize: 14 }}>
            {guides.length
              ? "All of this topic's content is in the guided walkthroughs above."
              : "No articles in this topic yet."}
          </div>
        )}
      </section>
    </div>
  );
}

export default KbCategory;
